import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { bootstrapNegociacaoDetail, cancelNegociacaoStart, encerrarAtendimentoHumanoDetail, encerrarNegociacaoDetail, fetchFluxoDaNegociacao, fetchNegociacaoAiInsights, fetchNegociacaoCasoDetail, fetchNegociacaoConversaId, fetchNegociacaoMarcos, fetchNegociacaoTelefones, fetchOutrasNegociacoes, reabrirNegociacaoDetail, sendResumeTemplate } from '../store/actions';
import { selecionarCanalVisualizado } from '../store/slice';
import { selectNegociacaoDetailAiInsights, selectNegociacaoDetailAiInsightsError, selectNegociacaoDetailAiInsightsLoading, selectNegociacaoDetailCancelStart, selectNegociacaoDetailCaso, selectCanalVisualizadoId, selectFluxoDaNegociacao, selectNegociacaoDetailConversaId, selectNegociacaoTelefones, selectNegociacaoTelefonesCasoIdBuscado, selectNegociacaoTelefonesError, selectNegociacaoTelefonesLoading, selectTelefoneDoCanalVisualizado, selectNegociacaoDetailError, selectNegociacaoDetailIsEncerrando, selectNegociacaoDetailIsEncerrandoAtendimento, selectNegociacaoDetailIsReabrindo, selectNegociacaoDetailLoading, selectIsResumingJanela, selectResumeJanelaError, selectOutrasNegociacoes, selectOutrasNegociacoesError, selectOutrasNegociacoesIsLoading, selectOutrasNegociacoesPage, selectOutrasNegociacoesTotal, selectOutrasNegociacoesTotalPages } from '../store/selectors';
import { selectAuthUser } from '@/features/auth';
import { selectCurrentEscritorio } from '@/features/escritorio/store/crud/selectors';
import type { Mensagem } from '@/features/messaging';
import { useMessagingRealtime } from '@/features/messaging/hooks/useMessagingRealtime';
import { fetchMensagens, pauseConversaAutomation, refreshConversaByEntity, resumeConversaAutomation, sendNegociacaoMessage } from '@/features/messaging/store/actions';
import { selectConversaById, selectLoadingMensagens, selectMensagensDaConversa, selectTotalMensagensDaConversa } from '@/features/messaging/store/selectors';
import { useToast } from '@/shared/providers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchColunasDisponiveis, fetchColunasPreferencia, selectColunasDisponiveisEscritorioId, selectColunasDisponiveisLoaded, selectColunasPreferenciaEscritorioId, selectColunasPreferenciaLoaded, selectColunasSelecionadasOrdenadas } from '@/features/negociacaoColunas';
import { pauseNegociacaoCaso, resumeNegociacaoCaso, type TipoEncerramento } from '@/features/negociacoes';
import type { ChatMessageUi, IaStatusUi, LastMensagemUi, NegotiationDetails, NegotiationSummaryUi, SituacaoAtualUi } from '../types/negociacaoDetailUiTypes';
import { getMensagemRecebidaAposEncerramento } from '../utils/mensagemAposEncerramento';
import { formatDateTimeFull } from '@/shared/formatters';
import { formatDateTime } from '@/shared/utils/dateUtils';
import { buildIaStatusUi, buildLastMensagemUi, buildNegotiationDetails, buildNegotiationSummaryUi, buildSituacaoAtualUi, canSendResumeTemplate, filterMensagensForCaso, filterMensagensForChat, getIsClosed, getLastMensagem, getMensagemCaseId, getSendDisabledReason, getTemperatureColorClass, isJanelaAguardandoRetomada, isJanelaBloqueada, isJanelaSemPrimeiraResposta, isReadOnlyStatus, mapChatMessages, sortMensagensByCreatedAt } from '../utils/negociacaoDetailUiUtils';
import { devePedirFilaDeTelefones } from '../utils/devePedirFilaDeTelefones';
import type { NegociacaoDetailTab } from '../types/negociacaoDetailTypes';
interface UseNegociacaoDetailPageInput {
  caseId: string;
  initialTab?: NegociacaoDetailTab;
}
export const useNegociacaoDetailPage = ({
  caseId,
  initialTab = 'resumo'
}: UseNegociacaoDetailPageInput) => {
  const dispatch = useAppDispatch();
  const {
    showError,
    showSuccess
  } = useToast();
  const caso = useAppSelector(selectNegociacaoDetailCaso);
  const conversaId = useAppSelector(selectNegociacaoDetailConversaId);
  const telefones = useAppSelector(selectNegociacaoTelefones);
  const telefonesLoading = useAppSelector(selectNegociacaoTelefonesLoading);
  const telefonesError = useAppSelector(selectNegociacaoTelefonesError);
  const telefonesCasoIdBuscado = useAppSelector(selectNegociacaoTelefonesCasoIdBuscado);
  const canalVisualizadoId = useAppSelector(selectCanalVisualizadoId);
  /**
   * O telefone do canal que a aba Chat está mostrando — NÃO `caso.counterpartyPhone`.
   *
   * A mensagem é gravada em `conversaId` mas ENTREGUE em `phone`. Com fila de telefones, usar o
   * número ativo do caso enquanto o operador olha outro canal manda a mensagem para a pessoa
   * errada no instante em que ele abre um canal antigo.
   */
  const telefoneDoCanalVisualizado = useAppSelector(selectTelefoneDoCanalVisualizado);
  const aiInsights = useAppSelector(selectNegociacaoDetailAiInsights);
  const isLoadingAiInsights = useAppSelector(selectNegociacaoDetailAiInsightsLoading);
  const aiInsightsError = useAppSelector(selectNegociacaoDetailAiInsightsError);
  const isLoading = useAppSelector(selectNegociacaoDetailLoading);
  const error = useAppSelector(selectNegociacaoDetailError);
  const cancelStart = useAppSelector(selectNegociacaoDetailCancelStart);
  const isEncerrando = useAppSelector(selectNegociacaoDetailIsEncerrando);
  const isEncerrandoAtendimento = useAppSelector(selectNegociacaoDetailIsEncerrandoAtendimento);
  const isReabrindo = useAppSelector(selectNegociacaoDetailIsReabrindo);
  const isResumingJanela = useAppSelector(selectIsResumingJanela);
  const resumeJanelaError = useAppSelector(selectResumeJanelaError);
  const flowId = caso?.start?.flowId;
  const fluxo = useAppSelector(selectFluxoDaNegociacao);
  const flow = flowId && fluxo.flowId === flowId ? fluxo.data : null;
  const isFlowLoading = fluxo.status === 'loading';
  const conversa = useAppSelector(selectConversaById(conversaId || ''));
  const mensagens = useAppSelector(selectMensagensDaConversa(conversaId || ''));
  const totalMensagens = useAppSelector(selectTotalMensagensDaConversa(conversaId || ''));
  const isLoadingMensagens = useAppSelector(selectLoadingMensagens);
  const mensagensPorConversa = useAppSelector(state => state.messaging.mensagensPorConversa);
  const usuarioAtualId = useAppSelector(selectAuthUser)?.id ?? null;
  const outrasNegociacoes = useAppSelector(selectOutrasNegociacoes);
  const outrasIsLoading = useAppSelector(selectOutrasNegociacoesIsLoading);
  const outrasError = useAppSelector(selectOutrasNegociacoesError);
  const outrasPage = useAppSelector(selectOutrasNegociacoesPage);
  const outrasTotal = useAppSelector(selectOutrasNegociacoesTotal);
  const outrasTotalPages = useAppSelector(selectOutrasNegociacoesTotalPages);
  const hasOutrasNegociacoes = outrasTotal > 1;
  useEffect(() => {
    if (!caso?.id) return;
    dispatch(fetchOutrasNegociacoes({
      caseId: caso.id,
      page: 1
    }));
  }, [caso?.id, dispatch]);
  const setOutrasPage = useCallback((nextPage: number) => {
    if (!caso?.id) return;
    dispatch(fetchOutrasNegociacoes({
      caseId: caso.id,
      page: nextPage
    }));
  }, [caso?.id, dispatch]);
  const [activeTab, setActiveTab] = useState<NegociacaoDetailTab>(initialTab);
  const [chatMessage, setChatMessage] = useState('');
  const previousTemperatureRef = useRef<string | null>(null);
  const abaAtivaRef = useRef(activeTab);
  useEffect(() => {
    abaAtivaRef.current = activeTab;
  }, [activeTab]);
  const refreshCaseSnapshot = useCallback((targetCaseId: string) => {
    dispatch(fetchNegociacaoCasoDetail(targetCaseId));
    if (abaAtivaRef.current === 'linha_do_tempo') {
      dispatch(fetchNegociacaoMarcos({
        caseId: targetCaseId
      }));
    }
  }, [dispatch]);
  useEffect(() => {
    if (!flowId) return;
    dispatch(fetchFluxoDaNegociacao(flowId));
  }, [flowId, dispatch]);
  const colunasExtras = useAppSelector(selectColunasSelecionadasOrdenadas);
  const colunasDisponiveisLoaded = useAppSelector(selectColunasDisponiveisLoaded);
  const colunasDisponiveisEscritorioId = useAppSelector(selectColunasDisponiveisEscritorioId);
  const colunasPreferenciaLoaded = useAppSelector(selectColunasPreferenciaLoaded);
  const colunasPreferenciaEscritorioId = useAppSelector(selectColunasPreferenciaEscritorioId);
  const colunasEscritorioId = caso?.escritorioId;
  useEffect(() => {
    if (!colunasEscritorioId) return;
    if (!colunasDisponiveisLoaded || colunasDisponiveisEscritorioId !== colunasEscritorioId) {
      dispatch(fetchColunasDisponiveis(colunasEscritorioId));
    }
    if (!colunasPreferenciaLoaded || colunasPreferenciaEscritorioId !== colunasEscritorioId) {
      dispatch(fetchColunasPreferencia(colunasEscritorioId));
    }
  }, [colunasEscritorioId, colunasDisponiveisLoaded, colunasDisponiveisEscritorioId, colunasPreferenciaLoaded, colunasPreferenciaEscritorioId, dispatch]);
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  const user = useAppSelector(state_0 => state_0.auth.user);
  const currentEscritorio = useAppSelector(selectCurrentEscritorio);
  const realtimeEscritorioId = caso?.escritorioId || currentEscritorio?.id || undefined;
  const {
    isConnected
  } = useMessagingRealtime(user?.empresaId || '', realtimeEscritorioId);
  useEffect(() => {
    if (!caseId) return;
    dispatch(bootstrapNegociacaoDetail({
      caseId
    }));
  }, [caseId, dispatch]);
  const prevCasoStatusRef = useRef<string | null>(null);
  useEffect(() => {
    const currentStatus = caso?.status ?? null;
    const prevStatus = prevCasoStatusRef.current;
    if (prevStatus === 'SCHEDULED' && currentStatus !== 'SCHEDULED' && caso && !conversaId) {
      dispatch(fetchNegociacaoConversaId({
        caso
      }));
    }
    prevCasoStatusRef.current = currentStatus;
  }, [caso, conversaId, dispatch]);
  useEffect(() => {
    if (!conversaId) return;
    dispatch(fetchMensagens({
      conversaId
    }));
  }, [conversaId, dispatch]);
  const prevMensagensCountRef = useRef(0);
  const lastSyncedCaseMessageRef = useRef<string | null>(null);
  useEffect(() => {
    const currentCount = mensagens.length;
    if (prevMensagensCountRef.current === 0) {
      prevMensagensCountRef.current = currentCount;
      return;
    }
    if (currentCount <= prevMensagensCountRef.current) {
      prevMensagensCountRef.current = currentCount;
      return;
    }
    prevMensagensCountRef.current = currentCount;
    const handle = setTimeout(() => {
      if (caseId) {
        refreshCaseSnapshot(caseId);
      }
    }, 3_000);
    return () => clearTimeout(handle);
  }, [caseId, mensagens.length, refreshCaseSnapshot]);
  const expiryHandledRef = useRef<string | null>(null);
  useEffect(() => {
    const status = caso?.janelaAtendimento?.status;
    const expiraEm = caso?.janelaAtendimento?.expiraEm;
    if (!caseId || status !== 'ABERTA' || !expiraEm) {
      return;
    }
    const msUntilExpiry = new Date(expiraEm).getTime() - Date.now();
    if (msUntilExpiry <= 0) {
      if (expiryHandledRef.current === expiraEm) return;
      expiryHandledRef.current = expiraEm;
      refreshCaseSnapshot(caseId);
      return;
    }
    const handle_0 = setTimeout(() => {
      expiryHandledRef.current = expiraEm;
      refreshCaseSnapshot(caseId);
    }, msUntilExpiry);
    return () => clearTimeout(handle_0);
  }, [caseId, caso?.janelaAtendimento?.status, caso?.janelaAtendimento?.expiraEm, refreshCaseSnapshot]);
  const mensagensDaNegociacao = useMemo(() => {
    if (!caso?.id) return [];
    return Object.values(mensagensPorConversa).flat().filter(mensagem => getMensagemCaseId(mensagem) === caso.id);
  }, [caso?.id, mensagensPorConversa]);
  const lastMensagemDaNegociacao = useMemo<Mensagem | null>(() => {
    return getLastMensagem(mensagensDaNegociacao);
  }, [mensagensDaNegociacao]);
  useEffect(() => {
    if (!caso || !lastMensagemDaNegociacao) return;
    if (lastMensagemDaNegociacao.conversaId === conversaId) return;
    if (lastSyncedCaseMessageRef.current === lastMensagemDaNegociacao.id) return;
    lastSyncedCaseMessageRef.current = lastMensagemDaNegociacao.id;
    dispatch(fetchNegociacaoConversaId({
      caso
    }));
    refreshCaseSnapshot(caso.id);
  }, [caso, conversaId, dispatch, lastMensagemDaNegociacao, refreshCaseSnapshot]);
  const retryLoad = useCallback(() => {
    if (!caseId) return;
    dispatch(bootstrapNegociacaoDetail({
      caseId
    }));
  }, [caseId, dispatch]);
  const negotiationUi = useMemo<NegotiationSummaryUi | null>(() => {
    return buildNegotiationSummaryUi(caso);
  }, [caso]);
  const currentTemperature = negotiationUi?.temperature ?? null;
  useEffect(() => {
    if (!caseId || !currentTemperature) return;
    const previousTemperature = previousTemperatureRef.current;
    if (previousTemperature === null) {
      previousTemperatureRef.current = currentTemperature;
      return;
    }
    if (previousTemperature === currentTemperature) {
      return;
    }
    previousTemperatureRef.current = currentTemperature;
    void dispatch(fetchNegociacaoAiInsights({
      caseId,
      forceRefresh: true
    }));
  }, [caseId, currentTemperature, dispatch]);
  const canCancelStart = useMemo(() => {
    return Boolean(caso?.start && caso.start.status === 'PENDING');
  }, [caso?.start]);
  const handleCancelStart = useCallback(async () => {
    if (!caso) {
      showError('Erro', 'Negociação não carregada');
      return;
    }
    if (!canCancelStart) {
      showError('Erro', 'Início não pode ser cancelado');
      return;
    }
    const result = await dispatch(cancelNegociacaoStart({
      caseId: caso.id
    }));
    if (cancelNegociacaoStart.fulfilled.match(result)) {
      showSuccess('Início cancelado');
      refreshCaseSnapshot(caso.id);
      return;
    }
    const errorMessage = typeof result.payload === 'string' ? result.payload : result.error?.message || 'Erro ao cancelar início';
    showError('Erro', errorMessage);
  }, [canCancelStart, caso, dispatch, refreshCaseSnapshot, showError, showSuccess]);
  const handlePauseCaso = useCallback(async () => {
    if (!caso) {
      showError('Erro', 'Negociação não carregada');
      return false;
    }
    const result_0 = await dispatch(pauseNegociacaoCaso({
      caseId: caso.id
    }));
    if (pauseNegociacaoCaso.fulfilled.match(result_0)) {
      showSuccess('Negociação pausada');
      refreshCaseSnapshot(caso.id);
      if (user?.empresaId) {
        dispatch(refreshConversaByEntity({
          empresaId: user.empresaId,
          escritorioId: caso.escritorioId,
          entityType: 'negociacaoCaso',
          entityId: caso.id
        }));
      }
      return true;
    }
    const errorMessage_0 = typeof result_0.payload === 'string' ? result_0.payload : result_0.error?.message || 'Erro ao pausar negociação';
    showError('Erro', errorMessage_0);
    return false;
  }, [caso, dispatch, refreshCaseSnapshot, showError, showSuccess, user?.empresaId]);
  const handleResumeCaso = useCallback(async () => {
    if (!caso) {
      showError('Erro', 'Negociação não carregada');
      return false;
    }
    const result_1 = await dispatch(resumeNegociacaoCaso({
      caseId: caso.id
    }));
    if (resumeNegociacaoCaso.fulfilled.match(result_1)) {
      showSuccess('Negociação reiniciada');
      refreshCaseSnapshot(caso.id);
      if (user?.empresaId) {
        dispatch(refreshConversaByEntity({
          empresaId: user.empresaId,
          escritorioId: caso.escritorioId,
          entityType: 'negociacaoCaso',
          entityId: caso.id
        }));
      }
      return true;
    }
    const errorMessage_1 = typeof result_1.payload === 'string' ? result_1.payload : result_1.error?.message || 'Erro ao reiniciar negociação';
    showError('Erro', errorMessage_1);
    return false;
  }, [caso, dispatch, refreshCaseSnapshot, showError, showSuccess, user?.empresaId]);
  const handleEncerrarCaso = useCallback(async (tipo: TipoEncerramento, observacoes: string, detalhesAcordo: {
    valorAcordado: number;
    parcelas?: number;
    primeiroVencimento?: string;
  } | undefined, encerrarAtendimentoHumano: boolean) => {
    if (!caso) {
      showError('Erro', 'Negociação não carregada');
      throw new Error('Negociação não carregada');
    }
    const result_2 = await dispatch(encerrarNegociacaoDetail({
      caseId: caso.id,
      tipo,
      observacoes,
      detalhesAcordo,
      encerrarAtendimentoHumano
    }));
    if (encerrarNegociacaoDetail.fulfilled.match(result_2)) {
      showSuccess('Negociação encerrada');
      refreshCaseSnapshot(caso.id);
      return;
    }
    const errorMessage_2 = typeof result_2.payload === 'string' ? result_2.payload : result_2.error?.message || 'Erro ao encerrar negociação';
    showError('Erro', errorMessage_2);
    throw new Error(errorMessage_2);
  }, [caso, dispatch, refreshCaseSnapshot, showError, showSuccess]);
  const handleReabrirCaso = useCallback(async (manterDadosAnteriores = true, justificativa = 'Reabertura manual') => {
    if (!caso) {
      showError('Erro', 'Negociação não carregada');
      throw new Error('Negociação não carregada');
    }
    const result_3 = await dispatch(reabrirNegociacaoDetail({
      caseId: caso.id,
      justificativa,
      manterDadosAnteriores
    }));
    if (reabrirNegociacaoDetail.fulfilled.match(result_3)) {
      showSuccess('Negociação reaberta');
      refreshCaseSnapshot(caso.id);
      return;
    }
    const errorMessage_3 = typeof result_3.payload === 'string' ? result_3.payload : result_3.error?.message || 'Erro ao reabrir negociação';
    showError('Erro', errorMessage_3);
    throw new Error(errorMessage_3);
  }, [caso, dispatch, refreshCaseSnapshot, showError, showSuccess]);
  const handleEncerrarAtendimento = useCallback(async () => {
    if (!caso) {
      showError('Erro', 'Negociação não carregada');
      throw new Error('Negociação não carregada');
    }
    const result_4 = await dispatch(encerrarAtendimentoHumanoDetail(caso.id));
    if (encerrarAtendimentoHumanoDetail.fulfilled.match(result_4)) {
      showSuccess('Atendimento humano encerrado');
      return;
    }
    const errorMessage_4 = typeof result_4.payload === 'string' ? result_4.payload : result_4.error?.message || 'Erro ao encerrar atendimento';
    showError('Erro', errorMessage_4);
    throw new Error(errorMessage_4);
  }, [caso, dispatch, showError, showSuccess]);
  const isIAPaused = caso?.isHumanIntervention ?? (conversa?.mode || 'BOT') === 'HUMAN';
  const toggleAutomation = useCallback(async () => {
    if (!conversaId) return;
    if (isIAPaused) {
      await dispatch(resumeConversaAutomation({
        conversaId
      }));
    } else {
      await dispatch(pauseConversaAutomation({
        conversaId
      }));
    }
    if (caso?.id) {
      dispatch(fetchNegociacaoCasoDetail(caso.id));
    }
  }, [caso?.id, conversaId, dispatch, isIAPaused]);
  const handleSendMessage = useCallback(() => {
    if (!caso || !conversaId) return;
    const text = chatMessage.trim();
    if (!text) return;
    if (!isIAPaused) {
      dispatch(pauseConversaAutomation({
        conversaId
      }));
    }
    dispatch(sendNegociacaoMessage({
      conversaId: canalVisualizadoId ?? conversaId,
      caseId: caso.id,
      phone: telefoneDoCanalVisualizado ?? caso.counterpartyPhone,
      text
    }));
    setChatMessage('');
  }, [canalVisualizadoId, caso, chatMessage, conversaId, dispatch, isIAPaused, telefoneDoCanalVisualizado]);

  /**
   * Carrega a fila sob demanda — o painel lateral só pede quando o link "+N canais" é aberto.
   * Caso legado responde 200 com lista vazia, e a UI cai no telefone único do caso.
   */
  const carregarTelefones = useCallback(() => {
    if (!caso) return;
    void dispatch(fetchNegociacaoTelefones(caso.id));
  }, [caso, dispatch]);

  /**
   * A aba Chat precisa da fila para saber se há outro canal a oferecer — e o painel lateral
   * carrega só no clique. Buscar aqui, ao abrir o chat, é o que faz a barra de canal existir
   * sem obrigar o operador a passar pelo painel primeiro. A guarda de "uma vez por caso" vive
   * em `devePedirFilaDeTelefones`.
   */
  useEffect(() => {
    if (!caso) return;
    const pedido = {
      activeTab,
      casoId: caso.id,
      casoIdBuscado: telefonesCasoIdBuscado,
      isLoading: telefonesLoading
    };
    if (!devePedirFilaDeTelefones(pedido)) return;
    void dispatch(fetchNegociacaoTelefones(caso.id));
  }, [activeTab, caso, dispatch, telefonesCasoIdBuscado, telefonesLoading]);
  const selecionarCanal = useCallback((conversaIdDoCanal: string | null) => {
    dispatch(selecionarCanalVisualizado(conversaIdDoCanal));
  }, [dispatch]);
  const isClosed = getIsClosed(caso?.status);
  const isArchived = caso?.status === 'ARCHIVED' || Boolean(caso?.archivedAt);
  const isReadOnly = isReadOnlyStatus(caso?.status) || isArchived;
  const isScheduled = caso?.status === 'SCHEDULED';
  const janelaAtendimento = caso?.janelaAtendimento ?? null;
  const janelaBloqueada = isJanelaBloqueada(janelaAtendimento?.status);
  const canToggleAutomation = Boolean(conversaId) && !isReadOnly && !isScheduled;
  const canSendMessage = Boolean(conversaId) && !isReadOnly && !isScheduled && !janelaBloqueada;
  const janelaStatus = janelaAtendimento?.status ?? null;
  const canResumeWindow = canSendResumeTemplate({
    status: caso?.status,
    archivedAt: caso?.archivedAt,
    conversaId,
    janelaStatus
  });
  const janelaNuncaRespondeu = isJanelaSemPrimeiraResposta(janelaAtendimento);
  const sendDisabledReason = getSendDisabledReason({
    isScheduled: Boolean(isScheduled),
    isReadOnly,
    isArchived,
    conversaId,
    janelaAguardandoRetomada: isJanelaAguardandoRetomada(janelaStatus)
  });
  const handleResumeWindow = useCallback(() => {
    if (!caseId || !canResumeWindow || isResumingJanela) return;
    dispatch(sendResumeTemplate(caseId));
  }, [dispatch, caseId, canResumeWindow, isResumingJanela]);
  const startStatus = String(caso?.start?.status || '').toUpperCase();
  const startScheduledAt = caso?.start?.startScheduledFor || caso?.start?.scheduledFor;
  const startScheduledLabel = startScheduledAt ? formatDateTimeFull(startScheduledAt) || startScheduledAt : null;
  const isStartPending = caso?.status === 'SCHEDULED' && (startStatus === 'PENDING' || startStatus === 'PROCESSING');
  const isStartFailed = startStatus === 'FAILED';
  const isStartCancelled = startStatus === 'CANCELLED';
  const hasPendingStart = Boolean(canCancelStart);
  const negotiation = useMemo<NegotiationDetails | null>(() => {
    return buildNegotiationDetails({
      caso,
      negotiationSummaryUi: negotiationUi,
      channel: conversa?.canal,
      strategy: aiInsights?.strategy || null,
      recommendation: aiInsights?.recommendation || null,
      flowId: flow?.id ?? null
    });
  }, [aiInsights?.recommendation, aiInsights?.strategy, caso, conversa?.canal, negotiationUi, flow?.id]);
  const dataAtualizacaoLabel = useMemo(() => {
    return formatDateTime(caso?.dataAtualizacao);
  }, [caso?.dataAtualizacao]);
  const mensagensForCaso = useMemo(() => {
    return filterMensagensForCaso({
      casoId: caso?.id,
      caseCreatedAt: caso?.dataCriacao,
      mensagens
    });
  }, [caso?.dataCriacao, caso?.id, mensagens]);
  const mensagensForChat = useMemo(() => {
    return filterMensagensForChat(mensagensForCaso);
  }, [mensagensForCaso]);
  const mensagensForChatSorted = useMemo(() => {
    return sortMensagensByCreatedAt(mensagensForChat);
  }, [mensagensForChat]);
  const chatMessages = useMemo<ChatMessageUi[]>(() => {
    return mapChatMessages({
      mensagens: mensagensForChatSorted,
      clientName: negotiation?.client || 'Contraparte',
      usuarioAtualId
    });
  }, [mensagensForChatSorted, negotiation?.client, usuarioAtualId]);
  const lastMensagem = useMemo<Mensagem | null>(() => {
    return getLastMensagem(mensagensForCaso);
  }, [mensagensForCaso]);
  const mensagemRecebidaAposEncerramento = useMemo(() => getMensagemRecebidaAposEncerramento({
    isClosed,
    mensagemId: caso?.ultimaMensagemAposEncerramentoId ?? null,
    encerramentoData: caso?.encerramento?.data ?? null,
    mensagens: mensagensForChatSorted
  }), [isClosed, caso?.ultimaMensagemAposEncerramentoId, caso?.encerramento?.data, mensagensForChatSorted]);
  const handleEnviarMensagemUnica = useCallback(async (texto: string): Promise<void> => {
    if (!caso || !conversaId) throw new Error('Conversa indisponível');
    if (!texto.trim()) throw new Error('Mensagem não pode ser vazia');
    const enviado = await dispatch(sendNegociacaoMessage({
      conversaId: canalVisualizadoId ?? conversaId,
      caseId: caso.id,
      phone: telefoneDoCanalVisualizado ?? caso.counterpartyPhone,
      text: texto.trim()
    })).unwrap();
    if (!enviado) throw new Error('Não foi possível enviar a resposta. Tente novamente.');
  }, [canalVisualizadoId, caso, conversaId, dispatch, telefoneDoCanalVisualizado]);
  const ultimaMensagemEmLabel = useMemo(() => {
    const value = lastMensagem?.createdAt ?? caso?.ultimaMensagemEm;
    const label = formatDateTime(value);
    return label === '-' ? '—' : label;
  }, [caso?.ultimaMensagemEm, lastMensagem?.createdAt]);
  const lastMensagemUi = useMemo<LastMensagemUi>(() => {
    return buildLastMensagemUi({
      lastMensagem,
      clientName: negotiation?.client || 'Contraparte',
      usuarioAtualId
    });
  }, [lastMensagem, negotiation?.client, usuarioAtualId]);
  const situacaoAtualUi = useMemo<SituacaoAtualUi>(() => {
    return buildSituacaoAtualUi({
      isStartFailed,
      isStartCancelled,
      isStartPending,
      startScheduledLabel,
      isIAPaused,
      lastMensagem
    });
  }, [isIAPaused, isStartCancelled, isStartFailed, isStartPending, lastMensagem, startScheduledLabel]);
  const iaStatusUi = useMemo<IaStatusUi>(() => {
    return buildIaStatusUi({
      isStartFailed,
      isStartCancelled,
      isStartPending,
      startScheduledLabel,
      isIAPaused
    });
  }, [isIAPaused, isStartCancelled, isStartFailed, isStartPending, startScheduledLabel]);
  const toggleAutomationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isToggleAutomationLockedRef = useRef(false);
  const [isToggleAutomationLoading, setIsToggleAutomationLoading] = useState(false);
  useEffect(() => {
    return () => {
      if (toggleAutomationTimeoutRef.current) {
        clearTimeout(toggleAutomationTimeoutRef.current);
        toggleAutomationTimeoutRef.current = null;
      }
      isToggleAutomationLockedRef.current = false;
    };
  }, []);
  const handleToggleAutomationClick = useCallback(async () => {
    if (!canToggleAutomation) return;
    if (isToggleAutomationLockedRef.current) return;
    isToggleAutomationLockedRef.current = true;
    setIsToggleAutomationLoading(true);
    try {
      await toggleAutomation();
    } finally {
      if (toggleAutomationTimeoutRef.current) {
        clearTimeout(toggleAutomationTimeoutRef.current);
      }
      toggleAutomationTimeoutRef.current = setTimeout(() => {
        isToggleAutomationLockedRef.current = false;
        setIsToggleAutomationLoading(false);
        toggleAutomationTimeoutRef.current = null;
      }, 600);
    }
  }, [canToggleAutomation, toggleAutomation]);
  return {
    activeTab,
    setActiveTab,
    telefones,
    telefonesLoading,
    telefonesError,
    canalVisualizadoId,
    telefoneDoCanalVisualizado,
    carregarTelefones,
    selecionarCanal,
    isLoading,
    isLoadingAiInsights,
    aiInsightsError,
    error: error ?? null,
    retryLoad,
    negotiationUi,
    negotiation,
    flow,
    isFlowLoading,
    caso,
    colunasExtras,
    dataAtualizacaoLabel,
    ultimaMensagemEmLabel,
    hasPendingStart,
    startScheduledLabel,
    isStartPending,
    isStartFailed,
    isStartCancelled,
    cancelStart,
    canCancelStart,
    handleCancelStart,
    handlePauseCaso,
    handleResumeCaso,
    handleEncerrarCaso,
    handleEncerrarAtendimento,
    handleReabrirCaso,
    isClosed,
    encerramento: caso?.encerramento ?? null,
    isEncerrando,
    isEncerrandoAtendimento,
    isReabrindo,
    mensagemRecebidaAposEncerramento,
    handleEnviarMensagemUnica,
    conversaId,
    conversa,
    mensagens,
    totalMensagens,
    isLoadingMensagens,
    chatMessages,
    mensagensForCaso,
    lastMensagemUi,
    situacaoAtualUi,
    iaStatusUi,
    chatMessage,
    setChatMessage,
    handleSendMessage,
    isIAPaused,
    handleToggleAutomationClick,
    isToggleAutomationLoading,
    canToggleAutomation,
    canSendMessage,
    sendDisabledReason,
    janelaStatus,
    canResumeWindow,
    janelaNuncaRespondeu,
    onResumeWindow: handleResumeWindow,
    isResumingJanela,
    resumeJanelaError,
    isConnected,
    isReadOnly,
    getTemperatureColorClass,
    outrasNegociacoes,
    outrasIsLoading,
    outrasError,
    outrasPage,
    outrasTotal,
    outrasTotalPages,
    hasOutrasNegociacoes,
    setOutrasPage
  };
};